import * as React from 'react'

import { MailIcon, PhoneIcon } from 'lucide-react'
import { makeObservable } from 'mobx'
import { inject, observer } from 'mobx-react'

import { PortalLoader } from '~/client/src/login/portal/components/PortalLoader'
import LoginCommonStore from '~/client/src/login/stores/ui/LoginCommon.store'
import {
  AuthFormError,
  AuthLayout,
} from '~/client/src/shared/components/AuthLayout'
import { Button } from '~/client/src/shared/components/Button/Button'
import { FieldSeparator } from '~/client/src/shared/components/shadcn-ui/field'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '~/client/src/shared/components/shadcn-ui/tabs'
import commonRoutes from '~/client/src/shared/constants/commonRoutes'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import ApiAuthService from '~/client/src/shared/services/ApiAuthService'
import AuthenticationStore from '~/client/src/shared/stores/domain/Authentication.store'
import { LOGOUT } from '~/client/src/shared/stores/EventStore/eventConstants'
import EventsStore from '~/client/src/shared/stores/EventStore/Events.store'
import { redirectToLogin } from '~/client/src/shared/utils/loginPortal'

import googleLogoUrl from './assets/google-logo.svg?url'
import microsoftLogoUrl from './assets/microsoft-logo.svg?url'
import procoreLogoUrl from './assets/procore-logo.svg?url'
import CodeVerificationPopup from './CodeInputDialog/CodeInputDialog'
import EmailPasswordForm from './Forms/EmailPasswordForm'
import PhoneNumberForm from './Forms/PhoneNumberForm'
import LoginViewStore, { OTPLength, SignInMethod } from './LoginView.store'

interface ILoginView {
  auth?: AuthenticationStore
  apiAuthService?: ApiAuthService
  common?: LoginCommonStore
  eventsStore?: EventsStore
}

@inject('auth', 'common', 'eventsStore', 'apiAuthService')
@observer
export default class LoginView extends React.Component<ILoginView> {
  private readonly loginViewStore: LoginViewStore = null
  private phoneLoginSubmitBtnRef: HTMLButtonElement = null

  public constructor(props: ILoginView) {
    super(props)

    const { auth, apiAuthService, common, eventsStore } = props
    this.loginViewStore = new LoginViewStore(
      eventsStore,
      auth,
      apiAuthService,
      common,
    )
    makeObservable(this)
  }

  public componentDidMount() {
    if (redirectToLogin({ pathname: commonRoutes.LOGIN })) {
      return
    }

    if (navigator.webdriver) {
      localStorage.setItem('lhTimeToLoginForm', performance.now().toString())
    }
  }

  public render() {
    if (this.props.eventsStore.appState.loading.get(LOGOUT)) {
      return (
        <PortalLoader
          hint={Localization.translator.loggingOut}
          className="tw:h-full"
        />
      )
    }

    const {
      isLoading,
      isCodeBeingVerified,
      codeVerificationErrorMsg,
      errorMessage,
      shouldShowCodeInput,
      handleCodeInputChange,
      hideVerificationCodeModal,
      enteredPhoneNumber,
    } = this.loginViewStore

    // Auth + tenants takes ~0.5-1s; the spinner alone reads as continuous with the
    // tenant bundle's route loader, where a dimmed form behind it read as a hang.
    if (isLoading && !shouldShowCodeInput) {
      return (
        <PortalLoader
          hint={Localization.translator.loggingIn}
          className="tw:h-full"
        />
      )
    }

    return (
      <AuthLayout showLanguageSelect title={Localization.translator.signIn}>
        <CodeVerificationPopup
          codeLength={OTPLength}
          phoneNumber={enteredPhoneNumber}
          isOpen={shouldShowCodeInput}
          onHide={hideVerificationCodeModal}
          errorMessage={codeVerificationErrorMsg}
          isCodeBeingVerified={isCodeBeingVerified}
          isCodeBeingResent={isLoading}
          onResendCode={this.triggerResendOtp}
          onCodeInputChange={handleCodeInputChange}
        />

        <div className="tw:flex tw:flex-col tw:gap-2">
          {this.renderSSOButtons()}
        </div>

        <FieldSeparator className="tw:h-4 tw:text-app-micro tw:uppercase">
          {Localization.translator.or}
        </FieldSeparator>

        <div className="tw:flex tw:flex-col tw:gap-4">
          {this.renderDirectMethods()}
          <AuthFormError>{errorMessage}</AuthFormError>
        </div>
      </AuthLayout>
    )
  }

  private renderDirectMethods(): JSX.Element {
    const {
      signInMethod,
      switchSignInMethod,
      signInWithEmailPassword,
      signInWithPhoneNumber,
      goToResetPasswordView,
      onEmailBlur,
    } = this.loginViewStore

    const activeMethod =
      signInMethod === SignInMethod.PhoneNumber
        ? SignInMethod.PhoneNumber
        : SignInMethod.EmailPassword

    return (
      <Tabs
        value={activeMethod}
        onValueChange={(value: SignInMethod) => switchSignInMethod(value)}
        className="tw:gap-4"
      >
        <TabsList variant="line" className="tw:w-full">
          {this.signInMethodTabs.map(({ page, title, icon }) => (
            <TabsTrigger key={page} value={page}>
              {icon}
              {title}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value={SignInMethod.EmailPassword}>
          <EmailPasswordForm
            onSubmit={signInWithEmailPassword}
            onResetPassword={goToResetPasswordView}
            onEmailBlur={onEmailBlur}
          />
        </TabsContent>
        <TabsContent value={SignInMethod.PhoneNumber}>
          <PhoneNumberForm
            setSubmitBtnRef={ref => (this.phoneLoginSubmitBtnRef = ref)}
            onSubmit={signInWithPhoneNumber}
          />
        </TabsContent>
      </Tabs>
    )
  }

  public triggerResendOtp = () => {
    this.loginViewStore.resetCodeVerificationErrorMessage()
    this.phoneLoginSubmitBtnRef.click()
  }

  private get signInMethodTabs() {
    return [
      {
        title: Localization.translator.email_noun,
        page: SignInMethod.EmailPassword,
        icon: <MailIcon />,
      },
      {
        title: Localization.translator.phone,
        page: SignInMethod.PhoneNumber,
        icon: <PhoneIcon />,
      },
    ]
  }

  private get ssoProviders() {
    const {
      signInWithMicrosoftSSO,
      signInWithProcoreSSO,
      signInWithGoogleSSO,
    } = this.loginViewStore

    return [
      {
        providerId: 'microsoft.com',
        title: Localization.translator.loginWithMicrosoft,
        logoUrl: microsoftLogoUrl,
        onClick: signInWithMicrosoftSSO,
      },
      {
        providerId: 'procore.com',
        title: Localization.translator.procoreLogin,
        logoUrl: procoreLogoUrl,
        onClick: signInWithProcoreSSO,
      },
      {
        providerId: 'google.com',
        title: Localization.translator.loginWithGoogle,
        logoUrl: googleLogoUrl,
        onClick: signInWithGoogleSSO,
      },
    ]
  }

  private renderSSOButtons(): JSX.Element[] {
    return this.ssoProviders.map(({ providerId, title, logoUrl, onClick }) => (
      <Button
        key={providerId}
        type="button"
        variant="outline"
        className="tw:w-full"
        onClick={onClick}
      >
        <img
          src={logoUrl}
          alt=""
          width={16}
          height={16}
          data-icon="inline-start"
          className="tw:size-4"
        />
        {title}
      </Button>
    ))
  }
}
